import { CalendarEvent } from '@src/types/Events';
import { Controller, useFormContext } from 'react-hook-form';

import {
  TextField,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { generateValues } from './FormSections/generateValues';
import storeConnector from '@src/store/storeConnector';
import moment from 'moment';
import { Stack as FracStack } from '@src/types/Stacks';
import { Dispatch, useEffect } from 'react';
import { colorOptions, StyledFormSection } from './FormSections/_elements';
import CommandsStackSection from './FormSections/CommandsStackSection';
import DateSection from './FormSections/DateSection';

interface AddEditEventFormProps {
  activeEvent: CalendarEvent;
  stacks: FracStack[];
  setDialogOpen: Dispatch<React.SetStateAction<boolean>>;
  setConfirmOpen: Dispatch<React.SetStateAction<boolean>>;
  handleEventUpdates: (eventToSubmit: CalendarEvent) => void;
}

function AddEditEventForm({
  activeEvent,
  stacks,
  handleEventUpdates,
}: AddEditEventFormProps) {
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<CalendarEvent>();

  useEffect(() => {
    reset(generateValues(activeEvent));
  }, [activeEvent, reset]);

  const onSubmit = (data: CalendarEvent) => {
    const eventToSubmit = {
      title: data.title,
      start: moment.utc(data.start).toISOString(),
      end: moment.utc(data.end).toISOString(),
      duration: data.duration,
      stackId: data.stackId,
      ...(data.startCommands?.length && {
        startCommands: data.startCommands,
      }),
      ...(data.stopCommands?.length && {
        stopCommands: data.stopCommands,
      }),
      backgroundColor: data.backgroundColor,
      ...(data.rrule?.freq
        ? {
            rrule: {
              ...data.rrule,
              dtstart: data.start,
            },
          }
        : undefined),
    };
    const diffInMinutes = moment(eventToSubmit.end).diff(
      moment(eventToSubmit.start),
      'minutes',
    );
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    eventToSubmit.duration = `${hours.toString().padStart(2, '0')}.${minutes.toString().padStart(2, '0')}`;

    return handleEventUpdates(eventToSubmit);
  };

  return (
    <>
      <Stack
        spacing={3}
        flex={1}
        divider={<Divider sx={{ border: '1px solid' }} />}
      >
        <StyledFormSection>
          <Typography variant='h6' sx={{ mt: 2 }}>
            General
          </Typography>
          <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
            <Controller
              name='title'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ flexGrow: 1, flexBasis: '66%' }}
                  label='Event Title'
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name='backgroundColor'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={{ flexShrink: 1, flexBasis: '34%' }}>
                  <InputLabel id='select-bgcolor'>Background Color</InputLabel>
                  <Select
                    {...field}
                    error={!!errors.backgroundColor}
                    labelId='select-bgcolor'
                    label='Background Color'
                  >
                    {colorOptions}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
        </StyledFormSection>

        <DateSection />

        <CommandsStackSection stacks={stacks} />
      </Stack>
      <Divider sx={{ border: '1px solid' }} />
      <StyledFormSection
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pb: 2,
        }}
      >
        <Button
          variant='contained'
          size='large'
          disabled={Object.keys(errors).length > 0}
          onClick={handleSubmit(onSubmit)}
          sx={{ mt: 2 }}
        >
          Save
        </Button>
      </StyledFormSection>
    </>
  );
}

export default storeConnector(AddEditEventForm, {
  config: ['siteMeta'],
  user: ['username', 'role'],
});
